import React, { useState, useEffect, useCallback } from 'react';
import adminService from '../services/adminService';
import api from '../services/api';
import toast from 'react-hot-toast';
import { ROLE_LABELS } from '../context/AuthContext';
import {
  HiOutlineCog,
  HiOutlineServer,
  HiOutlineDocumentText,
  HiOutlineClipboardCheck,
  HiOutlineUserGroup,
  HiOutlineDownload,
  HiOutlineRefresh,
  HiOutlineSave,
  HiOutlinePencil,
  HiOutlineX,
  HiOutlineKey,
  HiOutlineCheck,
  HiOutlineBan,
  HiOutlineEye,
  HiOutlineEyeOff,
  HiOutlineDatabase,
  HiOutlineChip,
  HiOutlineClock,
  HiOutlineShieldCheck,
  HiOutlineExclamation,
} from 'react-icons/hi';
import './AdminPanel.css';

const TABS = [
  { id: 'overview', label: 'Resumen', icon: <HiOutlineServer /> },
  { id: 'settings', label: 'Configuración', icon: <HiOutlineCog /> },
  { id: 'users', label: 'Usuarios', icon: <HiOutlineUserGroup /> },
  { id: 'ncf', label: 'NCF / Fiscal', icon: <HiOutlineDocumentText /> },
  { id: 'audit', label: 'Auditoría', icon: <HiOutlineClipboardCheck /> },
  { id: 'export', label: 'Exportar', icon: <HiOutlineDownload /> },
];

const CATEGORY_LABELS = {
  empresa: '🏢 Empresa',
  fiscal: '🧾 Fiscal',
  comisiones: '💰 Comisiones',
  inventario: '📦 Inventario',
  seguridad: '🔒 Seguridad',
  general: '⚙️ General',
};

const ROLES = ['admin', 'manager', 'salesman', 'warehouse', 'delivery', 'cashier', 'accountant'];

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  // Overview
  const [dashboard, setDashboard] = useState(null);

  // Settings
  const [settings, setSettings] = useState({});
  const [editedSettings, setEditedSettings] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);

  // Users
  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({});
  const [showPasswordModal, setShowPasswordModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // NCF
  const [ncfSequences, setNcfSequences] = useState([]);
  const [editingNcf, setEditingNcf] = useState(null);
  const [ncfForm, setNcfForm] = useState({});

  // Audit
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditPagination, setAuditPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [auditFilters, setAuditFilters] = useState({ action: '', entity_type: '', page: 1 });

  // Export
  const [exporting, setExporting] = useState(false);

  // ────────────────────────────────────────
  // DATA FETCHING
  // ────────────────────────────────────────
  const fetchDashboard = useCallback(async () => {
    try {
      const res = await adminService.getDashboard();
      setDashboard(res.data);
    } catch {
      toast.error('Error cargando dashboard admin');
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await adminService.getSettings();
      setSettings(res.data.grouped || {});
      setEditedSettings({});
    } catch {
      toast.error('Error cargando configuración');
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get('/auth/users');
      setUsers(res.data?.data || []);
    } catch {
      toast.error('Error cargando usuarios');
    }
  }, []);

  const fetchNcf = useCallback(async () => {
    try {
      const res = await adminService.getNcfSequences();
      setNcfSequences(res.data?.data || []);
    } catch {
      toast.error('Error cargando secuencias NCF');
    }
  }, []);

  const fetchAuditLogs = useCallback(async (filters = {}) => {
    try {
      const res = await adminService.getAuditLogs({ limit: 30, ...filters });
      setAuditLogs(res.data?.data || []);
      setAuditPagination(res.data?.pagination || { page: 1, pages: 1, total: 0 });
    } catch {
      toast.error('Error cargando auditoría');
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await fetchDashboard();
      setLoading(false);
    };
    load();
  }, [fetchDashboard]);

  useEffect(() => {
    if (activeTab === 'settings') fetchSettings();
    if (activeTab === 'users') fetchUsers();
    if (activeTab === 'ncf') fetchNcf();
    if (activeTab === 'audit') fetchAuditLogs(auditFilters);
    // eslint-disable-next-line
  }, [activeTab]);

  // ────────────────────────────────────────
  // SETTINGS HANDLERS
  // ────────────────────────────────────────
  const handleSettingChange = (key, value) => {
    setEditedSettings((prev) => ({ ...prev, [key]: value }));
  };

  const hasChanges = Object.keys(editedSettings).length > 0;

  const saveSettings = async () => {
    if (!hasChanges) return;
    setSavingSettings(true);
    try {
      const settingsArray = Object.entries(editedSettings).map(([key, value]) => ({ key, value }));
      await adminService.updateSettingsBulk(settingsArray);
      toast.success(`${settingsArray.length} configuración(es) guardada(s)`);
      await fetchSettings();
    } catch {
      toast.error('Error guardando configuración');
    } finally {
      setSavingSettings(false);
    }
  };

  // ────────────────────────────────────────
  // USER HANDLERS
  // ────────────────────────────────────────
  const startEditUser = (user) => {
    setEditingUser(user.id);
    setUserForm({ full_name: user.full_name, email: user.email, role: user.role });
  };

  const saveUser = async (id) => {
    try {
      await adminService.updateUser(id, userForm);
      toast.success('Usuario actualizado');
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error actualizando usuario');
    }
  };

  const handleToggleUser = async (id) => {
    try {
      const res = await adminService.toggleUser(id);
      toast.success(res.data.message);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error');
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast.error('Mínimo 8 caracteres');
      return;
    }
    try {
      await adminService.resetPassword(showPasswordModal, newPassword);
      toast.success('Contraseña cambiada');
      setShowPasswordModal(null);
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error');
    }
  };

  // ────────────────────────────────────────
  // NCF HANDLERS
  // ────────────────────────────────────────
  const startEditNcf = (seq) => {
    setEditingNcf(seq.id);
    setNcfForm({
      max_sequence: seq.max_sequence,
      authorized_from: seq.authorized_from ? seq.authorized_from.split('T')[0] : '',
      authorized_to: seq.authorized_to ? seq.authorized_to.split('T')[0] : '',
      is_active: seq.is_active,
    });
  };

  const saveNcf = async (id) => {
    try {
      await adminService.updateNcfSequence(id, ncfForm);
      toast.success('Secuencia NCF actualizada');
      setEditingNcf(null);
      fetchNcf();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error');
    }
  };

  // ────────────────────────────────────────
  // EXPORT HANDLERS
  // ────────────────────────────────────────
  const handleExport = async (entity) => {
    setExporting(true);
    try {
      const res = await adminService.exportData(entity);
      const { headers, data, filename } = res.data;

      if (!data || data.length === 0) {
        toast.error('No hay datos para exportar');
        return;
      }

      // Convertir a CSV
      const csvRows = [headers.join(',')];
      data.forEach((row) => {
        const values = headers.map((h) => {
          const val = row[h] === null || row[h] === undefined ? '' : String(row[h]);
          return `"${val.replace(/"/g, '""')}"`;
        });
        csvRows.push(values.join(','));
      });

      const blob = new Blob(['\ufeff' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${filename}.csv`;
      link.click();

      toast.success(`${data.length} registros exportados`);
    } catch {
      toast.error('Error exportando datos');
    } finally {
      setExporting(false);
    }
  };

  // ────────────────────────────────────────
  // HELPERS
  // ────────────────────────────────────────
  const formatUptime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${h}h ${m}m`;
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleString('es-DO', { dateStyle: 'short', timeStyle: 'short' });
  };

  const fmt = (n) => Number(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // ────────────────────────────────────────
  // RENDER
  // ────────────────────────────────────────
  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-state"><div className="loader"></div><p>Cargando panel admin...</p></div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1><HiOutlineShieldCheck /> Panel de Administración</h1>
          <p>Configuración, usuarios, fiscalización y auditoría del sistema</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`admin-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.icon} <span>{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="admin-content">
        {/* ═══════════ TAB: OVERVIEW ═══════════ */}
        {activeTab === 'overview' && dashboard && (
          <div className="admin-overview">
            {/* System info */}
            <div className="admin-section">
              <h3><HiOutlineChip /> Estado del Sistema</h3>
              <div className="stats-grid-4">
                <div className="stat-card-admin">
                  <span className="stat-label">Node.js</span>
                  <span className="stat-value">{dashboard.system.node_version}</span>
                </div>
                <div className="stat-card-admin">
                  <span className="stat-label">Uptime</span>
                  <span className="stat-value">{formatUptime(dashboard.system.uptime_seconds)}</span>
                </div>
                <div className="stat-card-admin">
                  <span className="stat-label">Memoria</span>
                  <span className="stat-value">{dashboard.system.memory_mb} MB</span>
                </div>
                <div className="stat-card-admin">
                  <span className="stat-label">Base de Datos</span>
                  <span className="stat-value">{dashboard.system.db_size_mb} MB</span>
                </div>
              </div>
            </div>

            {/* Counts */}
            <div className="admin-section">
              <h3><HiOutlineDatabase /> Resumen General</h3>
              <div className="stats-grid-4">
                <div className="stat-card-admin highlight-blue">
                  <span className="stat-value">{dashboard.counts.total_users || 0}</span>
                  <span className="stat-label">Usuarios</span>
                </div>
                <div className="stat-card-admin highlight-green">
                  <span className="stat-value">{dashboard.counts.total_products || 0}</span>
                  <span className="stat-label">Productos</span>
                </div>
                <div className="stat-card-admin highlight-purple">
                  <span className="stat-value">{dashboard.counts.total_clients || 0}</span>
                  <span className="stat-label">Clientes</span>
                </div>
                <div className="stat-card-admin highlight-orange">
                  <span className="stat-value">{dashboard.counts.total_orders || 0}</span>
                  <span className="stat-label">Órdenes Totales</span>
                </div>
              </div>
              <div className="stats-grid-4" style={{ marginTop: '12px' }}>
                <div className="stat-card-admin">
                  <span className="stat-value">{dashboard.counts.active_orders || 0}</span>
                  <span className="stat-label">Órdenes Activas</span>
                </div>
                <div className="stat-card-admin">
                  <span className="stat-value">{dashboard.counts.pending_commissions || 0}</span>
                  <span className="stat-label">Comisiones Pendientes</span>
                </div>
                <div className="stat-card-admin">
                  <span className="stat-value">RD$ {fmt(dashboard.counts.pending_commission_amount)}</span>
                  <span className="stat-label">Monto Comisiones</span>
                </div>
                <div className="stat-card-admin">
                  <span className="stat-value">RD$ {fmt(dashboard.counts.receivable_amount)}</span>
                  <span className="stat-label">Cuentas x Cobrar</span>
                </div>
              </div>
            </div>

            {/* Hoy */}
            {dashboard.today && (
              <div className="admin-section">
                <h3><HiOutlineClock /> Hoy</h3>
                <div className="stats-grid-4">
                  <div className="stat-card-admin highlight-green">
                    <span className="stat-value">{dashboard.today.orders_today || 0}</span>
                    <span className="stat-label">Órdenes Hoy</span>
                  </div>
                  <div className="stat-card-admin highlight-green">
                    <span className="stat-value">RD$ {fmt(dashboard.today.revenue_today)}</span>
                    <span className="stat-label">Ventas Hoy</span>
                  </div>
                </div>
              </div>
            )}

            {/* Usuarios por rol */}
            <div className="admin-section">
              <h3><HiOutlineUserGroup /> Usuarios por Rol</h3>
              <div className="mini-table">
                <table>
                  <thead><tr><th>Rol</th><th>Total</th><th>Activos</th></tr></thead>
                  <tbody>
                    {(dashboard.users_by_role || []).map((r) => (
                      <tr key={r.role}>
                        <td><span className={`role-badge role-${r.role}`}>{ROLE_LABELS[r.role] || r.role}</span></td>
                        <td>{r.count}</td>
                        <td>{r.active}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* NCF Status */}
            <div className="admin-section">
              <h3><HiOutlineDocumentText /> Estado NCF</h3>
              <div className="mini-table">
                <table>
                  <thead><tr><th>Tipo</th><th>Usados</th><th>Máximo</th><th>Restantes</th><th>Estado</th></tr></thead>
                  <tbody>
                    {(dashboard.ncf_status || []).map((n) => (
                      <tr key={n.ncf_type}>
                        <td><strong>{n.ncf_type}</strong></td>
                        <td>{n.current_sequence}</td>
                        <td>{Number(n.max_sequence).toLocaleString()}</td>
                        <td>{Number(n.remaining).toLocaleString()}</td>
                        <td>
                          <span className={`status-badge ${n.is_active ? 'status-active' : 'status-inactive'}`}>
                            {n.is_active ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Recent logins */}
            {dashboard.recent_logins && dashboard.recent_logins.length > 0 && (
              <div className="admin-section">
                <h3><HiOutlineShieldCheck /> Últimos Logins</h3>
                <div className="mini-table">
                  <table>
                    <thead><tr><th>Usuario</th><th>Email</th><th>Fecha</th><th>IP</th></tr></thead>
                    <tbody>
                      {dashboard.recent_logins.map((l, i) => (
                        <tr key={i}>
                          <td>{l.full_name || '-'}</td>
                          <td>{l.user_email}</td>
                          <td>{formatDate(l.created_at)}</td>
                          <td><code>{l.ip_address || '-'}</code></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <button className="btn-secondary" onClick={() => { fetchDashboard(); toast.success('Actualizado'); }}>
              <HiOutlineRefresh /> Refrescar
            </button>
          </div>
        )}

        {/* ═══════════ TAB: SETTINGS ═══════════ */}
        {activeTab === 'settings' && (
          <div className="admin-settings">
            {Object.entries(settings).map(([category, items]) => (
              <div key={category} className="admin-section">
                <h3>{CATEGORY_LABELS[category] || category}</h3>
                <div className="settings-grid">
                  {items.map((s) => (
                    <div key={s.key} className="setting-item">
                      <label>
                        <span className="setting-label">{s.description || s.key}</span>
                        <span className="setting-key">{s.key}</span>
                      </label>
                      {s.value === 'true' || s.value === 'false' ? (
                        <select
                          value={editedSettings[s.key] !== undefined ? editedSettings[s.key] : s.value}
                          onChange={(e) => handleSettingChange(s.key, e.target.value)}
                        >
                          <option value="true">Sí</option>
                          <option value="false">No</option>
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={editedSettings[s.key] !== undefined ? editedSettings[s.key] : s.value}
                          onChange={(e) => handleSettingChange(s.key, e.target.value)}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {hasChanges && (
              <div className="settings-actions">
                <span className="changes-count">{Object.keys(editedSettings).length} cambio(s) pendiente(s)</span>
                <button className="btn-secondary" onClick={() => setEditedSettings({})}>
                  <HiOutlineX /> Descartar
                </button>
                <button className="btn-primary" onClick={saveSettings} disabled={savingSettings}>
                  <HiOutlineSave /> {savingSettings ? 'Guardando...' : 'Guardar Cambios'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* ═══════════ TAB: USERS ═══════════ */}
        {activeTab === 'users' && (
          <div className="admin-users">
            <div className="admin-section">
              <h3><HiOutlineUserGroup /> Gestión de Usuarios ({users.length})</h3>
              <div className="table-responsive">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Nombre</th>
                      <th>Email</th>
                      <th>Rol</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className={!u.is_active ? 'row-inactive' : ''}>
                        <td>{u.id}</td>
                        <td>
                          {editingUser === u.id ? (
                            <input
                              type="text"
                              value={userForm.full_name}
                              onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })}
                              className="inline-input"
                            />
                          ) : (
                            u.full_name || u.username
                          )}
                        </td>
                        <td>
                          {editingUser === u.id ? (
                            <input
                              type="email"
                              value={userForm.email}
                              onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                              className="inline-input"
                            />
                          ) : (
                            u.email
                          )}
                        </td>
                        <td>
                          {editingUser === u.id ? (
                            <select
                              value={userForm.role}
                              onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                              className="inline-select"
                            >
                              {ROLES.map((r) => (
                                <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                              ))}
                            </select>
                          ) : (
                            <span className={`role-badge role-${u.role}`}>{ROLE_LABELS[u.role] || u.role}</span>
                          )}
                        </td>
                        <td>
                          <span className={`status-badge ${u.is_active ? 'status-active' : 'status-inactive'}`}>
                            {u.is_active ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td>
                          <div className="action-btns">
                            {editingUser === u.id ? (
                              <>
                                <button className="btn-icon btn-save" onClick={() => saveUser(u.id)} title="Guardar">
                                  <HiOutlineCheck />
                                </button>
                                <button className="btn-icon btn-cancel" onClick={() => setEditingUser(null)} title="Cancelar">
                                  <HiOutlineX />
                                </button>
                              </>
                            ) : (
                              <>
                                <button className="btn-icon" onClick={() => startEditUser(u)} title="Editar">
                                  <HiOutlinePencil />
                                </button>
                                <button className="btn-icon" onClick={() => setShowPasswordModal(u.id)} title="Cambiar contraseña">
                                  <HiOutlineKey />
                                </button>
                                <button
                                  className={`btn-icon ${u.is_active ? 'btn-danger' : 'btn-success'}`}
                                  onClick={() => handleToggleUser(u.id)}
                                  title={u.is_active ? 'Desactivar' : 'Activar'}
                                >
                                  {u.is_active ? <HiOutlineBan /> : <HiOutlineCheck />}
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Password modal */}
            {showPasswordModal && (
              <div className="modal-overlay" onClick={() => { setShowPasswordModal(null); setNewPassword(''); }}>
                <div className="modal-content modal-sm" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3>Cambiar Contraseña</h3>
                    <button className="modal-close" onClick={() => { setShowPasswordModal(null); setNewPassword(''); }}>
                      <HiOutlineX />
                    </button>
                  </div>
                  <div className="modal-form">
                    <p className="modal-subtitle">
                      Usuario: <strong>{users.find((u) => u.id === showPasswordModal)?.email}</strong>
                    </p>
                    <div className="form-group">
                      <label>Nueva Contraseña (mín. 8 caracteres)</label>
                      <div className="password-input-wrap">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Nueva contraseña"
                          minLength={8}
                        />
                        <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)}>
                          {showPassword ? <HiOutlineEyeOff /> : <HiOutlineEye />}
                        </button>
                      </div>
                    </div>
                    <button className="btn-primary full" onClick={handleResetPassword} disabled={!newPassword || newPassword.length < 8}>
                      <HiOutlineKey /> Cambiar Contraseña
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══════════ TAB: NCF ═══════════ */}
        {activeTab === 'ncf' && (
          <div className="admin-ncf">
            <div className="admin-section">
              <h3><HiOutlineDocumentText /> Secuencias NCF (DGII)</h3>
              <p className="section-subtitle">Administrar numeración de comprobantes fiscales</p>
              <div className="table-responsive">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Tipo</th>
                      <th>Nombre</th>
                      <th>Serie</th>
                      <th>Secuencia Actual</th>
                      <th>Máximo</th>
                      <th>Restantes</th>
                      <th>Autorizado Desde</th>
                      <th>Autorizado Hasta</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ncfSequences.map((seq) => (
                      <tr key={seq.id}>
                        <td><strong>{seq.ncf_type}</strong></td>
                        <td>{seq.type_name}</td>
                        <td>{seq.series_letter}</td>
                        <td>{seq.current_sequence}</td>
                        <td>
                          {editingNcf === seq.id ? (
                            <input
                              type="number"
                              value={ncfForm.max_sequence}
                              onChange={(e) => setNcfForm({ ...ncfForm, max_sequence: parseInt(e.target.value) })}
                              className="inline-input"
                              style={{ width: '120px' }}
                            />
                          ) : (
                            Number(seq.max_sequence).toLocaleString()
                          )}
                        </td>
                        <td>
                          <span className={Number(seq.remaining) < 100 ? 'text-danger' : ''}>
                            {Number(seq.remaining).toLocaleString()}
                            {Number(seq.remaining) < 100 && <HiOutlineExclamation style={{ marginLeft: 4 }} />}
                          </span>
                        </td>
                        <td>
                          {editingNcf === seq.id ? (
                            <input
                              type="date"
                              value={ncfForm.authorized_from}
                              onChange={(e) => setNcfForm({ ...ncfForm, authorized_from: e.target.value })}
                              className="inline-input"
                            />
                          ) : (
                            seq.authorized_from ? new Date(seq.authorized_from).toLocaleDateString('es-DO') : '-'
                          )}
                        </td>
                        <td>
                          {editingNcf === seq.id ? (
                            <input
                              type="date"
                              value={ncfForm.authorized_to}
                              onChange={(e) => setNcfForm({ ...ncfForm, authorized_to: e.target.value })}
                              className="inline-input"
                            />
                          ) : (
                            seq.authorized_to ? new Date(seq.authorized_to).toLocaleDateString('es-DO') : '-'
                          )}
                        </td>
                        <td>
                          {editingNcf === seq.id ? (
                            <select
                              value={ncfForm.is_active}
                              onChange={(e) => setNcfForm({ ...ncfForm, is_active: e.target.value === 'true' })}
                              className="inline-select"
                            >
                              <option value="true">Activo</option>
                              <option value="false">Inactivo</option>
                            </select>
                          ) : (
                            <span className={`status-badge ${seq.is_active ? 'status-active' : 'status-inactive'}`}>
                              {seq.is_active ? 'Activo' : 'Inactivo'}
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="action-btns">
                            {editingNcf === seq.id ? (
                              <>
                                <button className="btn-icon btn-save" onClick={() => saveNcf(seq.id)}><HiOutlineCheck /></button>
                                <button className="btn-icon btn-cancel" onClick={() => setEditingNcf(null)}><HiOutlineX /></button>
                              </>
                            ) : (
                              <button className="btn-icon" onClick={() => startEditNcf(seq)}><HiOutlinePencil /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════ TAB: AUDIT ═══════════ */}
        {activeTab === 'audit' && (
          <div className="admin-audit">
            <div className="admin-section">
              <h3><HiOutlineClipboardCheck /> Log de Auditoría</h3>
              <div className="audit-filters">
                <select
                  value={auditFilters.action}
                  onChange={(e) => {
                    const f = { ...auditFilters, action: e.target.value, page: 1 };
                    setAuditFilters(f);
                    fetchAuditLogs(f);
                  }}
                >
                  <option value="">Todas las acciones</option>
                  <option value="LOGIN">Login</option>
                  <option value="UPDATE_USER">Editar Usuario</option>
                  <option value="RESET_PASSWORD">Cambiar Contraseña</option>
                  <option value="ACTIVATE_USER">Activar Usuario</option>
                  <option value="DEACTIVATE_USER">Desactivar Usuario</option>
                  <option value="UPDATE_SETTING">Cambiar Config</option>
                  <option value="UPDATE_SETTINGS_BULK">Cambiar Configs</option>
                  <option value="UPDATE_NCF_SEQUENCE">Editar NCF</option>
                  <option value="EXPORT_DATA">Exportar Datos</option>
                </select>
                <select
                  value={auditFilters.entity_type}
                  onChange={(e) => {
                    const f = { ...auditFilters, entity_type: e.target.value, page: 1 };
                    setAuditFilters(f);
                    fetchAuditLogs(f);
                  }}
                >
                  <option value="">Todas las entidades</option>
                  <option value="user">Usuarios</option>
                  <option value="setting">Configuración</option>
                  <option value="ncf_sequence">NCF</option>
                  <option value="order">Órdenes</option>
                </select>
                <button className="btn-secondary btn-sm" onClick={() => fetchAuditLogs(auditFilters)}>
                  <HiOutlineRefresh /> Refrescar
                </button>
              </div>

              {auditLogs.length === 0 ? (
                <div className="empty-state-card">
                  <HiOutlineClipboardCheck className="empty-icon" />
                  <p>No hay registros de auditoría</p>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Usuario</th>
                          <th>Acción</th>
                          <th>Entidad</th>
                          <th>ID</th>
                          <th>Detalles</th>
                          <th>IP</th>
                        </tr>
                      </thead>
                      <tbody>
                        {auditLogs.map((log) => (
                          <tr key={log.id}>
                            <td className="text-nowrap">{formatDate(log.created_at)}</td>
                            <td>{log.user_name || log.user_email || '-'}</td>
                            <td><span className="audit-action">{log.action}</span></td>
                            <td>{log.entity_type || '-'}</td>
                            <td>{log.entity_id || '-'}</td>
                            <td className="audit-details">
                              {log.details ? (
                                <code>{JSON.stringify(log.details).substring(0, 80)}</code>
                              ) : '-'}
                            </td>
                            <td><code>{log.ip_address || '-'}</code></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {auditPagination.pages > 1 && (
                    <div className="pagination">
                      <button
                        disabled={auditPagination.page <= 1}
                        onClick={() => {
                          const f = { ...auditFilters, page: auditPagination.page - 1 };
                          setAuditFilters(f);
                          fetchAuditLogs(f);
                        }}
                      >
                        Anterior
                      </button>
                      <span>Página {auditPagination.page} de {auditPagination.pages} ({auditPagination.total} registros)</span>
                      <button
                        disabled={auditPagination.page >= auditPagination.pages}
                        onClick={() => {
                          const f = { ...auditFilters, page: auditPagination.page + 1 };
                          setAuditFilters(f);
                          fetchAuditLogs(f);
                        }}
                      >
                        Siguiente
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* ═══════════ TAB: EXPORT ═══════════ */}
        {activeTab === 'export' && (
          <div className="admin-export">
            <div className="admin-section">
              <h3><HiOutlineDownload /> Exportar Datos a CSV</h3>
              <p className="section-subtitle">Descarga los datos del sistema en formato CSV para respaldo o análisis</p>
              <div className="export-grid">
                {[
                  { entity: 'orders', label: 'Órdenes', desc: 'Todas las órdenes con cliente, vendedor, NCF y montos', icon: '📋' },
                  { entity: 'clients', label: 'Clientes', desc: 'Directorio completo de clientes con RNC y tipo', icon: '👥' },
                  { entity: 'products', label: 'Productos', desc: 'Catálogo con precios, stock y categorías', icon: '📦' },
                  { entity: 'commissions', label: 'Comisiones', desc: 'Historial de comisiones por vendedor', icon: '💰' },
                  { entity: 'expenses', label: 'Gastos', desc: 'Registro de gastos con categoría y proveedor', icon: '🧾' },
                ].map((item) => (
                  <div key={item.entity} className="export-card">
                    <div className="export-card-header">
                      <span className="export-icon">{item.icon}</span>
                      <h4>{item.label}</h4>
                    </div>
                    <p>{item.desc}</p>
                    <button
                      className="btn-primary"
                      onClick={() => handleExport(item.entity)}
                      disabled={exporting}
                    >
                      <HiOutlineDownload /> {exporting ? 'Exportando...' : 'Descargar CSV'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
